// CurrentSlideEditor.js
// Renders ONE slide's heading/subheading/bullets as a self-contained card.
// SlideGenerator renders one of these per slide in a horizontally
// scrolling strip below the video player — each instance owns its own
// edit state, so editing one card never touches the others.

import React, { useState } from 'react';

function CurrentSlideEditor({ slide, slideIndex, totalSlides, isActive, onCopyText, onUpdateSlide }) {
  const [copied, setCopied] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState({ heading: '', subheading: '', bulletPoints: [] });

  if (!slide) return null;

  const content = slide.extractedContent || {};
  const pad2 = (n) => String(n).padStart(2, '0');

  const handleCopy = () => {
    onCopyText(slide);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const startEditing = () => {
    setDraft({
      heading: content.heading || '',
      subheading: content.subheading || '',
      bulletPoints: content.bulletPoints ? [...content.bulletPoints] : []
    });
    setIsEditing(true);
  };

  const cancelEditing = () => setIsEditing(false);

  const saveEditing = () => {
    const cleanedBullets = draft.bulletPoints
      .map(b => b.trim())
      .filter(b => b.length > 0);

    onUpdateSlide(slideIndex, {
      heading: draft.heading.trim(),
      subheading: draft.subheading.trim(),
      bulletPoints: cleanedBullets
    });

    setIsEditing(false);
  };

  const handleBulletChange = (bulletIndex, value) => {
    setDraft(prev => {
      const updated = [...prev.bulletPoints];
      updated[bulletIndex] = value;
      return { ...prev, bulletPoints: updated };
    });
  };

  const handleAddBullet = () => {
    setDraft(prev => ({ ...prev, bulletPoints: [...prev.bulletPoints, ''] }));
  };

  const handleRemoveBullet = (bulletIndex) => {
    setDraft(prev => ({
      ...prev,
      bulletPoints: prev.bulletPoints.filter((_, i) => i !== bulletIndex)
    }));
  };

  return (
    <div className={`slide-card ${isActive ? 'slide-card-active' : ''}`}>
      <div className="slide-card-header">
        <span className="slide-card-index">
          {pad2(slideIndex + 1)}<span className="slide-card-index-total">/{pad2(totalSlides)}</span>
        </span>

        <div className="slide-content-actions">
          {!isEditing ? (
            <>
              <button
                className="edit-text-inline"
                onClick={(e) => { e.preventDefault(); startEditing(); }}
                title="Edit slide text"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                </svg>
              </button>
              <button
                className={`copy-text-inline ${copied ? 'copied' : ''}`}
                onClick={(e) => { e.preventDefault(); handleCopy(); }}
                title={copied ? 'Copied!' : 'Copy slide text'}
              >
                {copied ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                  </svg>
                )}
              </button>
            </>
          ) : (
            <>
              <button className="save-text-inline" onClick={(e) => { e.preventDefault(); saveEditing(); }} title="Save changes">
                Save
              </button>
              <button className="cancel-text-inline" onClick={(e) => { e.preventDefault(); cancelEditing(); }} title="Cancel">
                Cancel
              </button>
            </>
          )}
        </div>
      </div>

      {!isEditing ? (
        <div className="slide-content">
          {content.heading && (
            <div className="content-item content-item-heading">
              <p className="content-heading-text">{content.heading}</p>
            </div>
          )}

          {content.subheading && (
            <div className="content-item content-item-subheading">
              <p className="content-subheading-text">{content.subheading}</p>
            </div>
          )}

          {content.bulletPoints?.length > 0 && (
            <div className="content-item content-item-bullets">
              <div className="slide-divider" />
              <ul className="content-bullets-list">
                {content.bulletPoints.map((point, i) => (
                  <li key={i}>
                    <span className="bullet-marker">{pad2(i + 1)}</span>
                    <span className="bullet-text">{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : (
        <div className="slide-content slide-content-editing">
          <div className="content-item">
            <label className="edit-field-label">Heading</label>
            <input
              type="text"
              className="edit-field-input"
              value={draft.heading}
              onChange={(e) => setDraft(prev => ({ ...prev, heading: e.target.value }))}
              placeholder="Slide heading"
            />
          </div>

          <div className="content-item">
            <label className="edit-field-label">Subheading</label>
            <input
              type="text"
              className="edit-field-input"
              value={draft.subheading}
              onChange={(e) => setDraft(prev => ({ ...prev, subheading: e.target.value }))}
              placeholder="Slide subheading"
            />
          </div>

          <div className="content-item">
            <label className="edit-field-label">Points</label>
            <div className="edit-bullets-list">
              {draft.bulletPoints.map((point, i) => (
                <div key={i} className="edit-bullet-row">
                  <input
                    type="text"
                    className="edit-field-input"
                    value={point}
                    onChange={(e) => handleBulletChange(i, e.target.value)}
                    placeholder={`Bullet point ${i + 1}`}
                  />
                  <button
                    className="remove-bullet-btn"
                    onClick={(e) => { e.preventDefault(); handleRemoveBullet(i); }}
                    title="Remove bullet"
                  >
                    ×
                  </button>
                </div>
              ))}
              <button
                className="add-bullet-btn"
                onClick={(e) => { e.preventDefault(); handleAddBullet(); }}
              >
                + Add bullet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CurrentSlideEditor;